/**
 * documentDates — dates between the profile's pickers and the API.
 *
 * The API speaks `YYYY-MM-DD` (and `HH:MM:00`); AppDatePicker speaks Date.
 * Plain dates are read as LOCAL calendar dates, so a date never slips a day
 * west of UTC. Also: the expiry chip state, and the FAA flight review expiry
 * the server derives.
 */

/**
 * Expiry state for the status chip. `null` when the document has no expiry —
 * the caller renders nothing rather than inventing "Valid".
 */
export function expiryState(raw, now = new Date()) {
	if (!raw) return null;
	const d = new Date(raw);
	if (Number.isNaN(d.getTime())) return null;
	const days = Math.floor((d.getTime() - now.getTime()) / 86400000);
	if (days < 0) return { tone: 'danger', label: 'Expired', days };
	if (days <= 30) return { tone: 'warning', label: `Expires in ${days} d`, days };
	return { tone: 'success', label: 'Valid', days };
}

/**
 * Server date (+ optional time) → the picker's value (a plain Date is accepted
 * by AppDatePicker).
 *
 * `YYYY-MM-DD` is read as a LOCAL calendar date. `new Date('2025-01-15')`
 * parses it as UTC midnight, which west of Greenwich is the evening BEFORE —
 * the picker showed the 14th and the next save wrote the 14th back.
 *
 * @param {string|Date} raw `YYYY-MM-DD`, `YYYY-MM-DD HH:MM[:SS]`, or a Date
 * @param {string} [time] `HH:MM[:SS]` stored separately (renters insurance)
 */
export function toPickerValue(raw, time) {
	if (!raw) return null;
	if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
	const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(String(raw));
	if (m) {
		const t = /^(\d{1,2}):(\d{2})/.exec(String(time || ''));
		const hour = m[4] != null ? Number(m[4]) : t ? Number(t[1]) : 0;
		const minute = m[5] != null ? Number(m[5]) : t ? Number(t[2]) : 0;
		return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), hour, minute);
	}
	const d = new Date(raw);
	return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * AppDatePicker value → `YYYY-MM-DD`, the format every Symfony DTO validates
 * with `Assert\Date`. Returns null (not '') for an empty picker, because the
 * DTOs treat null as "leave it alone" and '' as an invalid date.
 */
export function toApiDate(v) {
	if (!v) return null;
	const pad = n => String(n).padStart(2, '0');
	if (v instanceof Date) {
		return Number.isNaN(v.getTime())
			? null
			: `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
	}
	if (v.year == null || v.month == null || v.day == null) return null;
	return `${v.year}-${pad(v.month)}-${pad(v.day)}`;
}

/**
 * When an FAA flight review dated `date` (`YYYY-MM-DD`) expires: 24 calendar
 * months on, to the end of that month — `YYYY-MM-DD`, or null without a date.
 * Same steps as the server (symphony FlightReviewManager, legacy
 * FlightReviewDates::prepareExpires): PHP's `+2 years` rolls 29 Feb into
 * March first, then `last day of this month`.
 */
export function faaReviewExpiry(date) {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date || ''));
	if (!m) return null;
	const shifted = new Date(Number(m[1]) + 2, Number(m[2]) - 1, Number(m[3]));
	return toApiDate(new Date(shifted.getFullYear(), shifted.getMonth() + 1, 0));
}

/** AppDatePicker value → `HH:MM:00` (the DTOs' `Assert\Time`), or null when empty. */
export function toApiTime(v) {
	if (!v) return null;
	const pad = n => String(n).padStart(2, '0');
	if (v instanceof Date) {
		return Number.isNaN(v.getTime()) ? null : `${pad(v.getHours())}:${pad(v.getMinutes())}:00`;
	}
	if (v.year == null || v.month == null || v.day == null) return null;
	return `${pad(v.hour || 0)}:${pad(v.minute || 0)}:00`;
}
